/**
 * Default Layout Configurations for React Grid Layout
 * Defines responsive layouts for all breakpoints and tabs
 *
 * Charts are automatically positioned based on their order in the JSON payload.
 * All charts use default width=6 and height=7 for consistency.
 */

import { CHART_IDS } from './layoutConstants';

/**
 * Generate layout items for a given breakpoint
 * Automatically calculates x and y positions based on chart order
 *
 * @param {Array<string>} chartIds - Array of chart IDs in order
 * @param {number} colsPerRow - Number of columns per row (e.g., 2 for lg breakpoint with 12 cols)
 * @param {number} width - Width of each chart (default: 6)
 * @param {number} height - Height of each chart (default: 7)
 * @param {number} minWidth - Minimum width (default: 3)
 * @returns {Array<Object>} Layout configuration array
 */
const generateLayout = (chartIds, colsPerRow, width = 6, height = 7, minWidth = 3) => {
    return chartIds.map((chartId, index) => {
        const row = Math.floor(index / colsPerRow);
        const col = index % colsPerRow;

        return {
            i: chartId,
            x: col * width,
            y: row * height,
            w: width,
            h: height,
            minW: minWidth,
            minH: 1
        };
    });
};

// Chart IDs in order for Outcome Composition tab
const outcomeChartIds = [
    CHART_IDS.ORGANIC_VS_PAID,
    CHART_IDS.WATERFALL,
    CHART_IDS.ORGANIC_VS_PAID_TIME,
    CHART_IDS.PAID_DECOMP_TIME
];

// Chart IDs for Performance tab - first row (half-width each)
const performanceRow1ChartIds = [
    CHART_IDS.AVG_CAC,
    CHART_IDS.MARGINAL_CAC
];

// Chart IDs in order for Response Curve tab
const responseCurveChartIds = [
    CHART_IDS.RC_VS_ACTUAL
];

// Generate layouts for all breakpoints
const outcomeLayout = {
    lg: generateLayout(outcomeChartIds, 2, 6, 7, 3),      // 2 columns (12 cols / 6 width)
    md: generateLayout(outcomeChartIds, 2, 5, 7, 3),      // 2 columns (10 cols / 5 width)
    sm: generateLayout(outcomeChartIds, 2, 3, 7, 3),      // 2 columns (6 cols / 3 width)
    xs: generateLayout(outcomeChartIds, 2, 2, 7, 2)       // 2 columns (4 cols / 2 width)
};

// Performance: row 1 = two half-width charts, row 2 = spend chart full-width
const performanceLayout = {
    lg: [
        ...generateLayout(performanceRow1ChartIds, 2, 6, 7, 3),
        { i: CHART_IDS.SPEND_VS_ATTR_SHARE, x: 0, y: 7, w: 12, h: 7, minW: 6, minH: 1 }
    ],
    md: [
        ...generateLayout(performanceRow1ChartIds, 2, 5, 7, 3),
        { i: CHART_IDS.SPEND_VS_ATTR_SHARE, x: 0, y: 7, w: 10, h: 7, minW: 5, minH: 1 }
    ],
    sm: [
        ...generateLayout(performanceRow1ChartIds, 2, 3, 7, 3),
        { i: CHART_IDS.SPEND_VS_ATTR_SHARE, x: 0, y: 7, w: 6, h: 7, minW: 3, minH: 1 }
    ],
    xs: [
        ...generateLayout(performanceRow1ChartIds, 2, 2, 7, 2),
        { i: CHART_IDS.SPEND_VS_ATTR_SHARE, x: 0, y: 7, w: 4, h: 7, minW: 2, minH: 1 }
    ]
};

const responseCurveLayout = {
    lg: generateLayout(responseCurveChartIds, 1, 12, 7, 6),  // full width
    md: generateLayout(responseCurveChartIds, 1, 10, 7, 5),
    sm: generateLayout(responseCurveChartIds, 1, 6, 7, 3),
    xs: generateLayout(responseCurveChartIds, 1, 4, 7, 2)
};

export const DEFAULT_LAYOUTS = {
    outcome: outcomeLayout,
    performance: performanceLayout,
    response_curve: responseCurveLayout
};

export const DEFAULT_ALL_LAYOUTS = {
    lg: {
        outcome: outcomeLayout.lg,
        performance: performanceLayout.lg,
        response_curve: responseCurveLayout.lg
    },
    md: {
        outcome: outcomeLayout.md,
        performance: performanceLayout.md,
        response_curve: responseCurveLayout.md
    },
    sm: {
        outcome: outcomeLayout.sm,
        performance: performanceLayout.sm,
        response_curve: responseCurveLayout.sm
    },
    xs: {
        outcome: outcomeLayout.xs,
        performance: performanceLayout.xs,
        response_curve: responseCurveLayout.xs
    }
};
